export const errorType = {
  NO_RETRIABLE_UNEXPECTED_ERROR: "NO_RETRIABLE_UNEXPECTED_ERROR",
  RETRIABLE_UNEXPECTED_ERROR: "RETRIABLE_UNEXPECTED_ERROR",
  CLIENT_FIELDS_ERROR: "CLIENT_FIELDS_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
  WARNING_ERROR: "WARNING_ERROR",
} as const;

export type ErrorType = (typeof errorType)[keyof typeof errorType];
export type Error = {
  code: ErrorCode;
  category: ErrorType;
  fields?: ErrorField[];
  message?: string;
  data?: Record<string, unknown>[];
};
export type ErrorField = {
  name: string;
  message: string;
};
export type ErrorCode = (typeof errorCode)[keyof typeof errorCode];
export const errorCode = {
  SESSION_EXPIRED: "SESSION_EXPIRED",
  EXPIRED_JWT_TOKEN_ERROR: "EXPIRED_JWT_TOKEN_ERROR",
  INVALID_JWT_TOKEN_ERROR: "INVALID_JWT_TOKEN_ERROR",
  GENERIC_CLIENT_ERROR: "GENERIC_CLIENT_ERROR",
  SESSION_NOT_FOUND: "SESSION_NOT_FOUND",
  TOKEN_NOT_FOUND: "TOKEN_NOT_FOUND",
  INVALID_SESSION: "INVALID_SESSION",
  PATIENT_NOT_FOUND: "PATIENT_NOT_FOUND",
  DOCTOR_NOT_FOUND: "DOCTOR_NOT_FOUND",
  CANCELLATION_RESTRICTED: "CANCELLATION_RESTRICTED",
  PROVIDED_SERVICE_NOT_FOUND: "PROVIDED_SERVICE_NOT_FOUND",
  APPOINTMENT_NOT_FOUND: "APPOINTMENT_NOT_FOUND",
  USER_NOT_FOUND: "USER_NOT_FOUND",
  USER_ALREADY_EXISTS: "USER_ALREADY_EXISTS",
  SPECIALITY_ALREADY_EXISTS: "SPECIALITY_ALREADY_EXISTS",
  UNEXPECTED_ERROR: "UNEXPECTED_ERROR",
  INVALID_PAYLOAD: "INVALID_PAYLOAD",
  INVALID_PARAMETER: "INVALID_PARAMETER",
  INVALID_PASSWORD: "INVALID_PASSWORD",
  ROLE_NOT_FOUND: "ROLE_NOT_FOUND",
  ROLE_NOT_MATCH: "ROLE_NOT_MATCH",
  INVALID_KEY: "INVALID_KEY",
  UNAUTHORIZED: "UNAUTHORIZED",
  INVALID_JWT_TOKEN: "INVALID_JWT_TOKEN",
  UNSUPPORTED_JWT_TOKEN: "UNSUPPORTED_JWT_TOKEN",
  MALFORMED_JWT_TOKEN: "MALFORMED_JWT_TOKEN",
  INVALID_JWT_SIGNATURE: "INVALID_JWT_SIGNATURE",
  JWT_TOKEN_REVOKED: "JWT_TOKEN_REVOKED",
  INVALID_REFRESH_TOKEN: "INVALID_REFRESH_TOKEN",
  EXPIRED_JWT_TOKEN: "EXPIRED_JWT_TOKEN",
  JWT_TOKEN_NOT_FOUND: "JWT_TOKEN_NOT_FOUND",
  BAD_CREDENTIALS: "BAD_CREDENTIALS",
  ACCOUNT_NOT_ACTIVATED: "ACCOUNT_NOT_ACTIVATED",
  SPECIALITY_NOT_FOUND: "SPECIALITY_NOT_FOUND",
  FORBIDDEN_ACTION: "FORBIDDEN_ACTION",
  SPECIALTY_IN_USE: "SPECIALTY_IN_USE",
  ROLE_IN_USE: "ROLE_IN_USE",
  LICENCE_NOT_FOUND: "LICENCE_NOT_FOUND",
  DOCTOR_INVITATION_EMAIL_ALREADY_EXISTS:
    "DOCTOR_INVITATION_EMAIL_ALREADY_EXISTS",
  DOCTOR_INVITATION_PHONE_ALREADY_EXISTS:
    "DOCTOR_INVITATION_PHONE_ALREADY_EXISTS",
  DOCTOR_INVITATION_BULK_VALIDATION_FAILED:
    "DOCTOR_INVITATION_BULK_VALIDATION_FAILED",
  INVITATION_TRIAL_DAYS_INVALID: "INVITATION_TRIAL_DAYS_INVALID",
  PROMOTION_ALREADY_ASSIGNED_TO_FACILITY:
    "PROMOTION_ALREADY_ASSIGNED_TO_FACILITY",
  PROMOTION_FACILITY_HAS_NO_LICENCE: "PROMOTION_FACILITY_HAS_NO_LICENCE",
  PROMOTION_CODE_NOT_FOUND: "PROMOTION_CODE_NOT_FOUND",
  PROMOTION_CODE_ALREADY_EXISTS: "PROMOTION_CODE_ALREADY_EXISTS",
  PROMOTION_CODE_INVALID: "PROMOTION_CODE_INVALID",
  PROMOTION_REBATE_INVALID: "PROMOTION_REBATE_INVALID",
  PROMOTION_NOT_FOUND: "PROMOTION_NOT_FOUND",
  PROMOTION_INVALID_DATE_RANGE: "PROMOTION_INVALID_DATE_RANGE",
  PROMOTION_ASSIGNMENT_NOT_FOUND: "PROMOTION_ASSIGNMENT_NOT_FOUND",
};

export const InvalidSessionErrorsCodes = [
  "SESSION_EXPIRED",
  "EXPIRED_JWT_TOKEN_ERROR",
  "SESSION_NOT_FOUND",
  "TOKEN_NOT_FOUND",
  "INVALID_TOKEN",
  "INVALID_JWT_TOKEN_ERROR",
  "INVALID_JWT_TOKEN",
  "UNSUPPORTED_JWT_TOKEN",
  "MALFORMED_JWT_TOKEN",
  "INVALID_JWT_SIGNATURE",
  "JWT_TOKEN_REVOKED",
  "INVALID_REFRESH_TOKEN",
  "JWT_TOKEN_NOT_FOUND",
  "INVALID_SESSION",
];

export const ExpiredSessionErrorsCodes = [
  "SESSION_EXPIRED",
  "EXPIRED_JWT_TOKEN_ERROR",
  "EXPIRED_JWT_TOKEN",
];

/**
 * MED-1622: codes of the PIN lock. The lock screen handles them in its own UI
 * (or turns them into a lock / create / logout), never the global error modal.
 */
export const PinErrorCodes = [
  "PIN_REQUIRED",
  "SESSION_LOCKED",
  "PIN_INVALID",
  "PIN_ATTEMPTS_EXCEEDED",
  "PIN_ALREADY_SET",
  "PIN_INVALID_FORMAT",
  "PIN_RESET_OTP_INVALID",
  "PIN_RESET_RATE_LIMITED",
  "PIN_CREATE_REAUTH_REQUIRED",
  "SESSION_REUSE_DETECTED",
];

/**
 * Error codes that are handled silently by the caller.
 * The global error modal will NOT be shown for these codes.
 * Add a code here when a feature handles the error in its own UI.
 */
export const SilencedErrorCodes = [
  "LICENCE_NOT_FOUND",
  // MED-1471: "no pending invoice for this facility" is an ordinary answer for a TRIAL
  // licence, and the manager detail page says so in its own toast. The global modal's
  // wording ("the requested invoice could not be found") would also be misleading here.
  "INVOICE_NOT_FOUND",
  "DOCTOR_INVITATION_BULK_VALIDATION_FAILED",
  "APP_CONFIG_DUPLICATE_RULE",
  "PROMOTION_ALREADY_ASSIGNED_TO_FACILITY",
  // MED-1587: the doctor licences page explains a contract that has ended in its own banner.
  "DOCTOR_LICENCE_CONTRACT_ENDED",
  // MED-1596: the doctor licences page explains a cancellation with nothing to lift in its own banner.
  "DOCTOR_LICENCE_CANCELLATION_NOT_LIFTABLE",
  // MED-1622: the lock screen handles every PIN code itself.
  ...PinErrorCodes,
];
